<template>
  <div :class="{ 'disabled-area': !canEdit }">
    <v-overlay :value="loading">
      <v-progress-circular size="64" indeterminate />
    </v-overlay>

    <v-form
      ref="add-role"
      class="add-role"
      @submit.prevent="roleId ? onUpdate() : onCreate()"
    >
      <div class="add-role__col">
        <v-text-field
          v-model="formData.name"
          :label="$t('roles.role-name')"
          :rules="[isRequired]"
        />
        <v-textarea
          v-model="formData.description"
          no-resize
          :label="$t('common.table.description')"
        />
      </div>

      <h3 class="add-role__permissions-title">
        {{ $t('common.labels.permissions') }}
        <v-progress-circular
          v-if="permissionsLoading"
          class="ml-2"
          color="#464646"
          size="20"
          width="2.5"
          indeterminate
        />
      </h3>

      <ul class="add-role__permissions">
        <permissions-group
          v-for="(permission, key) in permissions"
          :key="key"
          v-model="formData.permissions"
          :permission="permission"
        />
      </ul>

      <div class="add-role__actions">
        <v-btn
          v-if="canEdit"
          type="submit"
          color="warning"
          depressed
          large
          :disabled="permissionsLoading || !formData.permissions.length"
        >
          {{ $t('common.btns.save') }}
        </v-btn>
      </div>
    </v-form>

    <v-snackbar v-model="snack.value" :color="snack.color" top right>
      {{ snack.message }}
    </v-snackbar>
  </div>
</template>

<script>
import PermissionsGroup from './PermissionsGroup.vue';

export default {
  props: {
    roleId: {
      type: String,
      default: null,
    },
  },
  components: { PermissionsGroup },
  data: () => ({
    loading: false,
    permissionsLoading: false,
    formData: {
      name: '',
      description: '',
      permissions: [],
    },
    snack: {
      value: false,
      message: '',
      color: '',
    },
  }),
  computed: {
    permissions() {
      return this.$store.getters['roles/permissions'];
    },
    canEdit() {
      const isAdmin = this.$store.getters['global/getRole'] === 'admin';
      const hasPermission = this.$can('role_edit');
      return isAdmin || hasPermission;
    },
  },
  methods: {
    isRequired(v) {
      return !!v || this.$t('common.fields.validation.field.required');
    },
    async fetchPermissions() {
      this.permissionsLoading = true;
      await this.$store.dispatch('roles/fetchPermissions');
      this.permissionsLoading = false;
    },
    async fetchRole() {
      this.loading = true;
      await this.$store.dispatch('roles/fetchRole', this.roleId);

      this.fillForm();
      this.loading = false;
    },
    fillForm() {
      const data = this.$store.state.roles.role;

      this.formData.name = data.displayName;
      this.formData.description = data.description;
      this.formData.permissions = data.permissions;
    },
    processResult({ success, error }) {
      const snack = {
        color: 'success',
        message: this.$t('common.saved'),
        value: true,
      };

      if (success) {
        setTimeout(() => {
          this.$router.push('/roles');
        }, 3000);
      }

      if (error) {
        snack.color = 'error';
        snack.message = this.$t('common.errors.APIerror');
      }

      this.snack = snack;
    },
    async onCreate() {
      const isValid = this.$refs['add-role'].validate();

      if (!isValid) return;

      this.loading = true;
      const result = await this.$store.dispatch('roles/addNew', this.formData);

      this.loading = false;
      this.processResult(result);
    },
    async onUpdate() {
      const isValid = this.$refs['add-role'].validate();

      if (!isValid) return;

      this.loading = true;
      const result = await this.$store.dispatch('roles/updateItem', {
        id: this.roleId,
        params: this.formData,
      });

      this.loading = false;
      this.processResult(result);
    },
  },
  created() {
    if (!this.permissions.length) {
      this.fetchPermissions();
    }

    if (this.roleId) this.fetchRole();
  },
};
</script>

<style lang="scss">
.add-role {
  margin-top: 20px;

  &__col {
    max-width: 350px;
  }

  &__permissions {
    display: flex;
    flex-wrap: wrap;
    padding: 0 !important;

    &-title {
      margin-top: 15px;
      margin-bottom: 15px;
      font-size: 20px;
      color: #464646;
    }
  }

  &__actions {
    margin-top: 30px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
